/**
 * Tehdaspalautus-dialogi (uudelleenkäytettävä).
 *
 * Käyttö:
 *   <FactoryResetDialog scope="module" moduleKey="invoicing" moduleLabel="Laskutus" />
 *   <FactoryResetDialog scope="global" />
 *
 * Esikatselee ensin kuinka paljon dataa poistettaisiin (GET /preview),
 * vaatii vahvistustekstin "TEHDASPALAUTUS" + salasanan, ja vasta sitten kutsuu
 * varsinaista palautusta.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2, Trash2, ShieldAlert, Eraser } from "lucide-react";
import { toast } from "sonner";

const CONFIRM_TEXT = "TEHDASPALAUTUS";

export default function FactoryResetDialog({
  open,
  onOpenChange,
  scope,            // "module" | "global"
  moduleKey,        // when scope==="module"
  moduleLabel,      // user-facing label
  onComplete,
}) {
  const [step, setStep] = useState("preview"); // preview | confirm | done
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [result, setResult] = useState(null);
  // GLOBAALIN tehdaspalautuksen lisätäpät – oletus: säilytä lisenssit ja käyttäjät
  const [alsoDeleteLicenses, setAlsoDeleteLicenses] = useState(false);
  const [alsoDeleteUsers, setAlsoDeleteUsers] = useState(false);
  const [destructiveAck, setDestructiveAck] = useState(false);

  useEffect(() => {
    if (!open) {
      // reset state on close
      setTimeout(() => {
        setStep("preview");
        setPreview(null);
        setConfirmText("");
        setPassword("");
        setResult(null);
        setAlsoDeleteLicenses(false);
        setAlsoDeleteUsers(false);
        setDestructiveAck(false);
      }, 200);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const url = scope === "module"
          ? `/factory-reset/preview/module/${moduleKey}`
          : "/factory-reset/preview";
        const r = await api.get(url);
        setPreview(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
        onOpenChange(false);
      }
      setLoading(false);
    })();
  }, [open, scope, moduleKey, onOpenChange]);

  const run = async () => {
    setLoading(true);
    try {
      const url = scope === "module"
        ? `/factory-reset/module/${moduleKey}`
        : "/factory-reset/global";
      const body = {
        confirm_text: confirmText,
        password,
      };
      if (scope === "global") {
        // Liput vain globaalille resetille:
        body.keep_licenses = !alsoDeleteLicenses;
        body.keep_users = !alsoDeleteUsers;
        // keep_other_data on master-lippu: jos KUMPIKAAN on päällä → keep_other_data=false
        body.keep_other_data = !(alsoDeleteLicenses || alsoDeleteUsers);
      }
      const r = await api.post(url, body);
      setResult(r.data);
      setStep("done");
      toast.success("Tehdaspalautus valmis");
      onComplete?.(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  // Globaali destruktiivinen valittu? Jos kyllä → vaatii ekstra-vahvistuksen
  const isDestructiveGlobal = scope === "global" && (alsoDeleteLicenses || alsoDeleteUsers);

  // Nollaa destruktiivinen ack jos täpät tyhjennetään (estää "stale ack" -tilanteen)
  useEffect(() => {
    if (!isDestructiveGlobal && destructiveAck) {
      setDestructiveAck(false);
    }
  }, [isDestructiveGlobal, destructiveAck]);

  const totalToDelete = scope === "module"
    ? (preview?.total || 0)
    : ((preview?.module_data_total || 0) + (preview?.other_users || 0) + (preview?.licenses || 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-red-400" />
            <DialogTitle className="text-red-200">
              Tehdaspalautus — {scope === "module" ? moduleLabel : "Koko Apps2Go"}
            </DialogTitle>
          </div>
          <DialogDescription>
            Tämä toiminto <strong className="text-red-300">poistaa pysyvästi</strong> alla olevat tiedot.
            Toimintoa ei voi peruuttaa.
          </DialogDescription>
        </DialogHeader>

        {loading && !preview ? (
          <div className="py-8 flex items-center gap-2 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" /> Lasketaan...
          </div>
        ) : step === "done" ? (
          <DoneView result={result} scope={scope} />
        ) : (
          <div className="space-y-4">
            {/* PREVIEW BLOCK */}
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 space-y-2">
              <p className="text-xs uppercase tracking-widest text-red-300 font-semibold flex items-center gap-2">
                <AlertTriangle className="w-3 h-3" /> Poistetaan
              </p>
              {scope === "module" ? (
                <PreviewModule preview={preview} moduleLabel={moduleLabel} />
              ) : (
                <PreviewGlobal preview={preview} />
              )}
              {totalToDelete === 0 && (
                <p className="text-white/40 text-xs italic">Ei mitään poistettavaa.</p>
              )}
            </div>

            {/* GLOBAL-tehdaspalautuksen lisätäpät */}
            {scope === "global" && (
              <div className="space-y-2">
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-2">
                  <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold">
                    Lisävalinnat (oletuksena säilytetään)
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer" data-testid="reset-also-licenses-label">
                    <input
                      type="checkbox"
                      checked={alsoDeleteLicenses}
                      onChange={(e) => setAlsoDeleteLicenses(e.target.checked)}
                      className="mt-0.5"
                      data-testid="reset-also-licenses"
                    />
                    <span className="text-xs">
                      <strong className="text-red-300">Poista myös kaikki lisenssit</strong>
                      <span className="block text-white/50">{preview?.licenses || 0} lisenssiä – peruuttamaton</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer" data-testid="reset-also-users-label">
                    <input
                      type="checkbox"
                      checked={alsoDeleteUsers}
                      onChange={(e) => setAlsoDeleteUsers(e.target.checked)}
                      className="mt-0.5"
                      data-testid="reset-also-users"
                    />
                    <span className="text-xs">
                      <strong className="text-red-300">Poista myös kaikki muut käyttäjät</strong>
                      <span className="block text-white/50">{preview?.other_users || 0} käyttäjää (lisenssiasiakkaat + heidän staffit + omat staffit)</span>
                    </span>
                  </label>
                </div>

                {/* Kun destruktiivinen valittu → näytä lisäakno joka vaatii eksplisiittisen ymmärryksen */}
                {isDestructiveGlobal && (
                  <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-3 space-y-2" data-testid="destructive-warning">
                    <p className="text-red-300 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Vaarallinen toiminto
                    </p>
                    <p className="text-xs text-white/80">
                      Olet poistamassa <strong className="text-red-300">kaikki asiakkaat / lisenssit järjestelmästä</strong>.
                      Tämä on peruuttamaton ja vaikuttaa muiden tilien dataan.
                    </p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={destructiveAck}
                        onChange={(e) => setDestructiveAck(e.target.checked)}
                        className="mt-0.5"
                        data-testid="reset-destructive-ack"
                      />
                      <span className="text-xs text-red-100">
                        Ymmärrän että tämä poistaa <strong>pysyvästi</strong> kaikkien asiakkaiden datan,
                        lisenssit ja kirjautumistunnukset. Ottavat asiakkaat menettävät pääsynsä järjestelmään.
                      </span>
                    </label>
                  </div>
                )}

                {/* Säilytys-info */}
                {!isDestructiveGlobal && (
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-200">
                    <p><strong>Säilytetään:</strong> kaikki lisenssit, asiakaskäyttäjät ja sovellustyökalut. Vain sinun (admin) oma testidatasi poistuu.</p>
                  </div>
                )}
              </div>
            )}

            {/* Yksittäisen moduulin reset */}
            {scope === "module" && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-200">
                <p><strong>Säilytetään:</strong> nykyinen admin-tunnuksesi & kaikki sovellustyökalut/-toiminnot. Vain valitun moduulin oma testidatasi poistetaan.</p>
              </div>
            )}

            {/* CONFIRMATION */}
            <div className="space-y-2 pt-2 border-t border-white/5">
              <div>
                <Label className="text-xs text-white/80">
                  Kirjoita tarkalleen: <code className="bg-red-500/10 text-red-300 px-1.5 py-0.5 rounded">{CONFIRM_TEXT}</code>
                </Label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_TEXT}
                  className="mt-1 font-mono"
                  data-testid="reset-confirm-text"
                />
              </div>
              <div>
                <Label className="text-xs text-white/80">Salasanasi</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  data-testid="reset-password"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="flex-1" data-testid="reset-cancel">
                Peruuta
              </Button>
              <Button
                onClick={run}
                disabled={loading || confirmText !== CONFIRM_TEXT || !password || (isDestructiveGlobal && !destructiveAck)}
                className={`flex-1 ${isDestructiveGlobal ? "bg-red-700 hover:bg-red-800" : "bg-red-500 hover:bg-red-600"} text-white inline-flex items-center gap-2`}
                data-testid="reset-confirm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {isDestructiveGlobal ? "Tehdaspalauta KAIKKI" : "Tehdaspalauta"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewModule({ preview, moduleLabel }) {
  if (!preview) return null;
  const entries = Object.entries(preview.collections || {});
  return (
    <ul className="text-sm space-y-1">
      <li className="text-white/90 font-medium">{moduleLabel}</li>
      {entries.map(([col, n]) => (
        <li key={col} className="text-white/60 text-xs flex items-center justify-between pl-2">
          <span><code>{col}</code></span>
          <span className="text-red-300 font-mono">{n}</span>
        </li>
      ))}
      <li className="text-white text-xs flex items-center justify-between pt-1 border-t border-white/5">
        <span>Yhteensä</span>
        <span className="text-red-300 font-mono font-bold">{preview.total}</span>
      </li>
    </ul>
  );
}

function PreviewGlobal({ preview }) {
  if (!preview) return null;
  return (
    <ul className="text-sm space-y-1.5">
      <li className="flex items-center justify-between text-white/80">
        <span>Liiketoimintadata (kaikki moduulit)</span>
        <span className="text-red-300 font-mono">{preview.module_data_total}</span>
      </li>
      <li className="flex items-center justify-between text-white/80">
        <span>Muut käyttäjät (admin/henkilökunta/asiakkaat)</span>
        <span className="text-red-300 font-mono">{preview.other_users}</span>
      </li>
      <li className="flex items-center justify-between text-white/80">
        <span>Lisenssit</span>
        <span className="text-red-300 font-mono">{preview.licenses}</span>
      </li>
    </ul>
  );
}

function DoneView({ result, scope }) {
  return (
    <div className="space-y-3 py-2">
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
        <p className="text-emerald-300 font-semibold">Tehdaspalautus suoritettu</p>
        {scope === "module" ? (
          <p className="text-white/70 mt-1">
            Poistettu yhteensä <strong className="text-red-300">{result.total_deleted}</strong> riviä moduulista <strong>{result.module}</strong>.
          </p>
        ) : (
          <>
            <p className="text-white/70 mt-1">
              Poistettu yhteensä <strong className="text-red-300">{result.total_deleted}</strong> kohdetta:
            </p>
            <ul className="text-xs text-white/60 mt-1 space-y-0.5">
              <li>Dataa: <span className="text-red-300 font-mono">{Object.values(result.deleted_data || {}).reduce((a, b) => a + b, 0)}</span> riviä</li>
              <li>Käyttäjiä: <span className="text-red-300 font-mono">{result.deleted_users}</span></li>
              <li>Lisenssejä: <span className="text-red-300 font-mono">{result.deleted_licenses}</span></li>
            </ul>
          </>
        )}
      </div>
      <p className="text-xs text-white/40">
        Voit nyt sulkea tämän ikkunan. Sivu päivitetään automaattisesti tarvittaessa.
      </p>
    </div>
  );
}


// =============================================================================
// MODULE SETTINGS BUTTON – siro nappi moduulisivuille
// =============================================================================
export function ModuleSettingsButton({ moduleKey, moduleLabel, onAfterReset }) {
  const [open, setOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-white/60 hover:text-white"
        title="Moduulin asetukset"
        data-testid={`module-settings-${moduleKey}`}
      >
        <Eraser className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{moduleLabel} – Asetukset</DialogTitle>
            <DialogDescription>
              Moduulikohtaiset toiminnot. Tehdaspalautus poistaa vain tämän moduulin tiedot — kaikki muut moduulit pysyvät koskemattomina.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
              <p className="text-xs uppercase tracking-widest text-red-300 font-semibold flex items-center gap-2 mb-2">
                <AlertTriangle className="w-3 h-3" /> Vaaravyöhyke
              </p>
              <p className="text-sm text-white/70 mb-3">
                Tehdaspalautus tyhjentää kaikki tämän moduulin tiedot pysyvästi.
              </p>
              <Button
                onClick={() => { setOpen(false); setResetOpen(true); }}
                variant="outline"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                data-testid={`module-reset-open-${moduleKey}`}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Tehdaspalauta moduuli
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FactoryResetDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        scope="module"
        moduleKey={moduleKey}
        moduleLabel={moduleLabel}
        onComplete={onAfterReset}
      />
    </>
  );
}
